/**
 * Shared lazily-loaded CaselawDB singleton.
 *
 * The opinions tab, the rules tab, and the citation cross-linker all query the
 * same offline database. They must share ONE instance — opening the OPFS
 * SAHPool VFS more than once (as separate per-feature loaders would) risks a
 * VFS name clash. The heavy sqlite-wasm module is dynamically imported on first
 * use so it never weighs on app startup.
 */
import type { CaselawDB } from './caselaw-db';

let dbPromise: Promise<CaselawDB | null> | null = null;

/** Open (once) and return the offline law database, or null if unavailable. */
export function getCaselawDb(): Promise<CaselawDB | null> {
  if (!dbPromise) dbPromise = import('./caselaw-db').then((m) => m.CaselawDB.load());
  return dbPromise;
}

/**
 * Force the next getCaselawDb() call to re-open from scratch, deleting the
 * on-device OPFS copy first so the fresh open re-imports rather than reusing
 * stale bytes. Called after a legal-content update is downloaded (see
 * app/content-updates.ts) so the newly-fetched caselaw.sqlite actually
 * takes effect instead of the previously-imported copy.
 */
export async function removeCaselawDbInstance(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db?.removeLocalCopy();
  }
  dbPromise = null;
}
